/**
 * Account Context
 *
 * Manages mining accounts state for the application.
 * Replaces WalletContext - all users now use Privy for authentication
 * and Evore subaccounts for mining operations (server-signed).
 */

import {
  ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { accountsAPI, MiningAccountSummary, AccountsListResponse } from '../api/client';

export interface AccountContextType {
  // Account state
  accounts: MiningAccountSummary[];
  selectedAccount: MiningAccountSummary | null;
  isLegacyMiner: boolean;
  isOG: boolean; // OG status for beta feature access
  isLoading: boolean;
  error: string | null;

  // Actions
  refreshAccounts: () => Promise<void>;
  selectAccount: (accountId: string | null) => void;
  createAccount: (displayName?: string, initialDeposit?: number) => Promise<MiningAccountSummary>;

  // Hidden accounts (hide from MiningDashboard tabs)
  hiddenAccountIds: Set<string>;
  toggleAccountVisibility: (accountId: string) => void;
  isAccountHidden: (accountId: string) => boolean;

  // Convenience flags
  hasAccounts: boolean;
  hasEvoreAccount: boolean;
  canMine: boolean; // true if has legacy account or at least one evore account
}

const AccountContext = createContext<AccountContextType>({
  accounts: [],
  selectedAccount: null,
  isLegacyMiner: false,
  isOG: false,
  isLoading: false,
  error: null,
  refreshAccounts: async () => {},
  selectAccount: () => {},
  createAccount: async () => { throw new Error('Not initialized'); },
  hiddenAccountIds: new Set(),
  toggleAccountVisibility: () => {},
  isAccountHidden: () => false,
  hasAccounts: false,
  hasEvoreAccount: false,
  canMine: false,
});

export function useAccounts() {
  return useContext(AccountContext);
}

interface AccountProviderProps {
  children: ReactNode;
}

function sortAccounts(accounts: MiningAccountSummary[]): MiningAccountSummary[] {
  return [...accounts].sort((a, b) => {
    // Legacy accounts first
    if (a.isLegacy !== b.isLegacy) return a.isLegacy ? -1 : 1;
    // Active (mining) before inactive
    if (a.isMining !== b.isMining) return a.isMining ? -1 : 1;
    // Alphabetical by displayName within same group
    const nameA = (a.displayName || '').toLowerCase();
    const nameB = (b.displayName || '').toLowerCase();
    return nameA.localeCompare(nameB, undefined, { numeric: true });
  });
}

export function AccountProvider({ children }: AccountProviderProps) {
  const { authenticated, getAccessToken } = usePrivy();

  const [accounts, setAccounts] = useState<MiningAccountSummary[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(() => {
    // Restore from localStorage
    return localStorage.getItem('selectedAccountId');
  });
  const [isLegacyMiner, setIsLegacyMiner] = useState(false);
  const [isOG, setIsOG] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hidden accounts state - load from localStorage
  const [hiddenAccountIds, setHiddenAccountIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('hiddenAccountIds');
      return new Set(stored ? JSON.parse(stored) : []);
    } catch {
      return new Set();
    }
  });

  // Derive selected account from ID
  const selectedAccount = accounts.find(a => a.id === selectedAccountId) || null;

  // Fetch accounts from server
  const refreshAccounts = useCallback(async () => {
    if (!authenticated) {
      setAccounts([]);
      setIsLegacyMiner(false);
      setIsOG(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('No access token');
      }

      const response = await accountsAPI.listAccounts(token) as AccountsListResponse;

      setAccounts(sortAccounts(response.accounts || []));
      setIsLegacyMiner(response.isLegacyMiner || false);
      setIsOG(response.isOG || false);

      // Auto-select first account if none selected
      if (!selectedAccountId && response.accounts.length > 0) {
        const firstAccount = response.accounts[0];
        setSelectedAccountId(firstAccount.id);
        localStorage.setItem('selectedAccountId', firstAccount.id);
      }

      // Clear selection if selected account no longer exists
      if (selectedAccountId && !response.accounts.find(a => a.id === selectedAccountId)) {
        setSelectedAccountId(null);
        localStorage.removeItem('selectedAccountId');
      }
    } catch (err: any) {
      console.error('Failed to fetch accounts:', err);
      setError(err.message || 'Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  }, [authenticated, getAccessToken, selectedAccountId]);

  // Select an account
  const selectAccount = useCallback((accountId: string | null) => {
    setSelectedAccountId(accountId);
    if (accountId) {
      localStorage.setItem('selectedAccountId', accountId);
    } else {
      localStorage.removeItem('selectedAccountId');
    }
  }, []);

  // Toggle account visibility (hide/show from MiningDashboard tabs)
  const toggleAccountVisibility = useCallback((accountId: string) => {
    setHiddenAccountIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      // Persist to localStorage
      localStorage.setItem('hiddenAccountIds', JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  }, []);

  // Check if account is hidden
  const isAccountHidden = useCallback((accountId: string) => {
    return hiddenAccountIds.has(accountId);
  }, [hiddenAccountIds]);

  // Create a new account
  const createAccount = useCallback(async (displayName?: string, initialDeposit?: number): Promise<MiningAccountSummary> => {
    if (!authenticated) {
      throw new Error('Not authenticated');
    }

    const token = await getAccessToken();
    if (!token) {
      throw new Error('No access token');
    }

    const response = await accountsAPI.createAccount(token, displayName, initialDeposit);

    if (!response.success) {
      throw new Error('Failed to create account');
    }

    // Create the new account object immediately
    const newAccount: MiningAccountSummary = {
      id: response.accountId,
      managerPubkey: response.managerPubkey,
      oreMinerPda: '', // Will be populated on refresh
      managedMinerAuth: response.managedMinerAuth || '', // Deposit address - populated on refresh if not returned
      accountType: 'evore_subaccount',
      displayName: displayName || 'Mining Account',
      isMining: false,
      isLegacy: false,
      isTransferable: true,
      createdAt: new Date().toISOString(),
      wasTransferred: false,
    };

    // Optimistically add the new account to state immediately
    setAccounts(prev => sortAccounts([...prev, newAccount]));

    // Select the new account
    setSelectedAccountId(response.accountId);
    localStorage.setItem('selectedAccountId', response.accountId);

    // Refresh in background to get complete data from server
    // (don't await - this prevents the flickering)
    refreshAccounts();

    return newAccount;
  }, [authenticated, getAccessToken, refreshAccounts]);

  // Load accounts on mount and when authentication changes
  useEffect(() => {
    refreshAccounts();
  }, [authenticated]); // Only depend on authenticated, not refreshAccounts to avoid loops

  // Derived state
  const hasAccounts = accounts.length > 0;
  const hasEvoreAccount = accounts.some(a => a.accountType === 'evore_subaccount');
  const canMine = isLegacyMiner || hasEvoreAccount;

  return (
    <AccountContext.Provider
      value={{
        accounts,
        selectedAccount,
        isLegacyMiner,
        isOG,
        isLoading,
        error,
        refreshAccounts,
        selectAccount,
        createAccount,
        hiddenAccountIds,
        toggleAccountVisibility,
        isAccountHidden,
        hasAccounts,
        hasEvoreAccount,
        canMine,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}
